/**
 * The title screen.
 *
 * A standalone surface over the whole viewport with none of the console on it —
 * no rail, no topbar, no readouts. The crawl runs down the middle and the four
 * choices sit beside it. Any of them dismisses the title and hands over to the
 * game.
 *
 * IT IS NOT A CONSOLE SCREEN. It was one, briefly, and that was wrong: a rail
 * key implies somewhere you can come back to at any time, alongside the world
 * and the factory, when this is the thing you pass through once on the way in.
 *
 * THE SIMULATION DOES NOT RUN WHILE IT IS UP. Reading the introduction should
 * not cost the player ticks of their colony.
 */

import { el } from './panels/panel';

export interface TitleChoice {
  /** Discard whatever is loaded and begin a new colony. */
  onNew(): void;
  /** Resume the saved session. Only offered when there is one. */
  onContinue(): void;
  /** Go straight to the Menu screen. */
  onSettings(): void;
  /** Halt the session. */
  onExit(): void;
  /** Describes the save, or null when the slot is empty. */
  savedSummary(): string | null;
}

/**
 * The crawl, one paragraph per element.
 *
 * THIS IS NOT A CRASH. The landing is deliberate, the site was chosen long
 * before anyone boarded, and the ship did exactly what it was built to do. What
 * makes it one-way is the posting, not a wreck: a Factory Operator is assigned
 * once and does not rotate out. That is the emotional register — someone who
 * volunteered to spend the rest of their life making a place other people will
 * live in, and who will hand it to them and stay.
 *
 * NO NUMBERS. Not how many drones, not how many mechs, not how many plots.
 * Every one of those is a balance figure that a difficulty setting will move,
 * and prose that states them goes quietly stale the first time one changes —
 * with nothing to catch it, because no test reads this file. The panels carry
 * the counts; the introduction carries the situation.
 *
 * WHY THIS PLACE: the Creators chose it for VARIETY rather than for wealth. A
 * colony does not live on one abundant ore, it lives on having some of
 * everything it will ever need — which is also, precisely, what the generator
 * guarantees (SPEC-ERRATA E18, E22: the landing site is prescanned and viable,
 * and every mineral is normalised against real demand). The fiction and the
 * world generator are saying the same thing.
 */
const INTRO: readonly string[] = [
  'You volunteered for this.',
  'The vessel was built to make one journey and then stop being a vessel. It came down under power onto a site the Creators chose long before anyone boarded, and drove its lower half into the ground exactly as intended. What stands above the soil is a pyramid: hull, and the refinery beneath it. It will not lift again. It was never going to.',
  'Neither will you. There is no return leg, no relief rotation, nobody coming to take the post. A Factory Operator is assigned once. You will spend the rest of your life on this ground, and the work is to make it somewhere other people can spend theirs.',
  'The Creators did not choose this place because it was rich. They chose it because it was varied. A colony does not survive on one abundant ore — it survives on having a little of everything it will ever need: the metal and the glass, the water, the salts that feed a field, the carbon that binds a wall. The surveys found all of it here, within reach of a single landing, and that is why the colonists were promised this sky and not another.',
  'That is the whole of the inheritance. Surveyed ground, and what lies under it. The drones do not choose what they dig; they take everything in the ten metres beneath them and bring it home, and what comes back is whatever that ground happened to hold.',
  'The refinery breaks the ore into elements and builds them back into things — beams and panes, wiring and concrete, water, fertilizer, soil. It cannot do all of that at once. Nothing here can.',
  'From that the mechs raise a city, plot by plot, and only ever outward from what already stands. Habitations and farms, water plants and power plants, factory districts, and somewhere for people to be people.',
  'The colonists are still in transit. They will arrive knowing nothing about ore ratios or power budgets; they will arrive expecting somewhere to live. When it can feed them, house them, water them and give them a reason to stay, you call them down.',
  'Until then it is you, and the drones, and the slow arithmetic of a machine that can only ever do one thing at a time.',
];

/** Pixels per second the crawl advances. Slow enough to read at a glance. */
const CRAWL_SPEED = 22;

export interface TitleScreen {
  root: HTMLElement;
  /** True while the title is covering the game. */
  isOpen(): boolean;
}

export function createTitleScreen(choice: TitleChoice): TitleScreen {
  const root = el('div', 'title-screen');

  const heading = el('div', 'title-mark');
  heading.append(
    el('h1', 'title-name', 'Heliocenter'),
    el('div', 'title-sub', 'Factory Operator — a one-way posting'),
    el('div', 'title-version', `Alpha ${__APP_VERSION__} · build ${__BUILD_ID__}`),
  );

  const viewport = el('div', 'crawl');
  const column = el('div', 'crawl-column');
  for (const paragraph of INTRO) column.append(el('p', 'crawl-line', paragraph));
  viewport.append(column);

  const menu = el('nav', 'title-menu');
  const saveLine = el('div', 'title-save');

  let open = true;

  function dismiss(action: () => void): void {
    action();
    open = false;
    root.classList.add('title-closed');
    // Removed rather than hidden: nothing behind it should be reachable
    // through a leftover overlay, and it is never shown again this session.
    window.setTimeout(() => root.remove(), 260);
  }

  function choiceButton(label: string, hint: string, action: () => void): HTMLButtonElement {
    const button = el('button', 'action title-key');
    button.append(el('span', 'title-key-label', label), el('span', 'title-key-hint', hint));
    button.addEventListener('click', () => {
      dismiss(action);
    });
    return button;
  }

  const saved = choice.savedSummary();

  const newGame = choiceButton('New', 'Begin a fresh colony', choice.onNew);
  const cont = choiceButton('Continue', 'Resume the saved session', choice.onContinue);
  const settings = choiceButton('Settings', 'Themes, saves, and the rest', choice.onSettings);
  const exit = choiceButton('Exit', 'Halt the session', choice.onExit);

  if (saved === null) {
    cont.disabled = true;
    saveLine.textContent = 'No saved session on this browser.';
  } else {
    saveLine.textContent = saved;
  }

  menu.append(newGame, cont, settings, exit);

  const body = el('div', 'title-body');
  body.append(viewport, el('div', 'title-side'));
  const side = body.lastElementChild as HTMLElement;
  side.append(menu, saveLine);

  root.append(heading, body);

  // --- the crawl ------------------------------------------------------------
  let offset = 0;
  let running = true;
  let lastFrame = 0;

  const maxOffset = (): number => Math.max(0, column.scrollHeight - viewport.clientHeight);

  function frame(now: number): void {
    const elapsed = lastFrame === 0 ? 0 : (now - lastFrame) / 1000;
    lastFrame = now;

    if (open && running && viewport.clientHeight > 0) {
      offset = Math.min(maxOffset(), offset + CRAWL_SPEED * elapsed);
      if (offset >= maxOffset()) running = false;
      column.style.transform = `translateY(${-offset}px)`;
    }

    if (open) window.requestAnimationFrame(frame);
  }
  window.requestAnimationFrame(frame);

  // A deliberate scroll hands control to the reader. Text that drags itself
  // away while you are reading it is worse than text that does not move.
  viewport.addEventListener('wheel', (event) => {
    running = false;
    offset = Math.min(maxOffset(), Math.max(0, offset + event.deltaY));
    column.style.transform = `translateY(${-offset}px)`;
  });

  return { root, isOpen: () => open };
}
